import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Icon from '@/lib/components/Icon';
import styles from './OutageBlock.module.scss';

/**
 * This component is displayed when a payment method has outage: true.
 * It replaces the PaymentMethodOption entirely with a neutral grey block
 * informing the user that the payment service is temporarily unavailable.
 *
 * When other non-outage payment methods are still available, the message
 * hints that the user can pick one of those alternatives; otherwise it
 * falls back to a generic "try again in a few minutes" copy.
 */
const OutageBlock = ({ name = null, icons, hasOtherPaymentMethods = false }) => {
    const { t } = useTranslation();

    const iconComponents = React.useMemo(
        () => icons?.map((icon) => (
            <Icon key={icon} id={icon} />
        )),
        [icons],
    );

    const messageKey = hasOtherPaymentMethods ? 'outage_PSP_with_alternatives' : 'outage_PSP';

    return (
        <div className={styles.outage_block}>
            <div className={styles.icon}>
                <Icon id="alert" />
            </div>
            <div className={styles.content}>
                <div className={styles.top_row}>
                    {!!name && <span className={styles.name}>{name}</span>}
                    {!!iconComponents?.length && (
                        <div className={styles.icons_row}>
                            {iconComponents.map((iconComponent, index) => (
                                <div key={`icon_${index}`} className={styles.icon_wrapper}>
                                    {iconComponent}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles.message}>
                    {t(messageKey)}
                </div>
            </div>
        </div>
    );
};

OutageBlock.propTypes = {
    /**
     * Payment method display name (e.g. "Carte bancaire", "PayPal"). Omit to show no name.
     */
    name: PropTypes.string,
    /**
     * Icon IDs to render via the existing Icon component (e.g. ['CB', 'Visa', 'MasterCard']).
     */
    icons: PropTypes.arrayOf(PropTypes.string),
    /**
     * When true, at least one other (non-outage) payment method is still available;
     * the message is then phrased to invite the user to try one of those alternatives.
     */
    hasOtherPaymentMethods: PropTypes.bool,
};

export default OutageBlock;
